<script setup lang="ts">
import { CenteredModal, IconCross, useIsTouchscreen, type ImageInfo } from '#shared'
import { useTemplateRef } from 'vue'

import('pinch-zoom-element')

const { isOpen, imageInfo } = defineProps<{ isOpen: boolean; imageInfo: ImageInfo | null }>()

const emit = defineEmits<{ close: [] }>()

const imageEl = useTemplateRef('imageEl')

const onCloseButtonClick = () => emit('close')

const onContentMouseDown = () => {
  emit('close')
}

const isTouchScreen = useIsTouchscreen()
const onImageMouseDown = (event: Event) => {
  if (isTouchScreen) return
  if (!(event instanceof MouseEvent)) return
  if (!imageEl.value) return

  const imageAspectRatio = imageEl.value.naturalWidth / imageEl.value.naturalHeight

  const imageBlockWidth = imageEl.value.clientWidth
  const imageBlockHeight = imageEl.value.clientHeight

  let renderedImageHeight = imageBlockWidth / imageAspectRatio
  let renderedImageWidth = imageBlockWidth

  if (renderedImageHeight > imageBlockHeight) {
    renderedImageHeight = imageBlockHeight
    renderedImageWidth = imageBlockHeight * imageAspectRatio
  }

  const verticalPadding = (imageBlockHeight - renderedImageHeight) / 2
  const horizontalPadding = (imageBlockWidth - renderedImageWidth) / 2

  if (event.offsetY < verticalPadding || event.offsetY > imageBlockHeight - verticalPadding) {
    emit('close')
  }

  if (event.offsetX < horizontalPadding || event.offsetX > imageBlockWidth - horizontalPadding) {
    emit('close')
  }
}

const imageWrapTag = isTouchScreen ? 'pinch-zoom' : 'div'
</script>

<template>
  <CenteredModal :modalClass="$style.root" :isOpen="isOpen" @close="emit('close')" kind="styleless">
    <div :class="$style.content" @mousedown="onContentMouseDown">
      <Component :is="imageWrapTag" :class="$style.imageWrap">
        <img
          v-if="imageInfo"
          :class="$style.image"
          :src="imageInfo.src"
          :alt="imageInfo.alt"
          ref="imageEl"
          @mousedown.stop="onImageMouseDown"
        />
      </Component>

      <button :class="$style.close" @click="onCloseButtonClick">
        <IconCross :class="$style.closeIcon" />
      </button>
    </div>
  </CenteredModal>
</template>

<style lang="scss" module>
.root {
  width: 100%;
  height: 100%;
}

.content {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imageWrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.image {
  width: calc(100% - 40px);
  height: calc(100% - 40px);
  object-fit: contain;
}

.close {
  top: 20px;
  right: 20px;
  position: absolute;
  display: flex;
  padding: 10px;
  background-color: #303035;
  border-radius: 4px;
  transition: color 0.1s;

  &:hover {
    color: #f44336;
  }
}

.closeIcon {
  width: 20px;
  height: 20px;
}
</style>
